import {
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  IconButton,
  IconButtonProps,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { RoleType, YouthKid } from "common";
import { useCallback, useRef } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { useIsInRole } from "../utils/useIsInRole";

interface YouthKidDeleteButtonProps extends IconButtonProps {
  youthKid: YouthKid;
}

export const YouthKidDeleteButton = ({
  youthKid,
  ...props
}: YouthKidDeleteButtonProps) => {
  const isAdmin = useIsInRole(RoleType.Admin);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  const api = useAPI();
  const { data, state, error, query } = useAPIQuery<"youthKidDelete">({
    apiCall: api.youthKidDelete,
  });
  const navigate = useNavigate();
  useToastOnAPIError({ title: "Unable to delete youth kid", error });
  useOnAPISuccess<"youthKidDelete">({
    data,
    state,
    onSuccess: () => {
      onClose();
      navigate("/people");
    },
  });

  const onDelete = useCallback(() => {
    query({ id: youthKid.id });
  }, [query, youthKid.id]);

  return (
    <>
      <IconButton
        colorScheme={"red"}
        icon={<MdDeleteOutline />}
        variant={"outline"}
        onClick={onOpen}
        {...props}
      />
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Youth Kid "{youthKid.fName} {youthKid.lName}"
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
              {!isAdmin ? (
                <Alert status="error" variant="left-accent" marginTop={2}>
                  <AlertIcon />
                  You must be an admin to delete youth kids
                </Alert>
              ) : null}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                isDisabled={!isAdmin}
                colorScheme="red"
                isLoading={state === "loading"}
                onClick={onDelete}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
